import { LayoutGrid, List, Sliders, Music2, Save, FolderOpen, Undo, Redo, Copy, Scissors, Menu } from 'lucide-react';
import { useStudioStore } from '@/store/useStudioStore';
import { PresenceIndicators } from './PresenceIndicators';

interface ToolbarProps {
  showEffectsRack: boolean;
  setShowEffectsRack: (show: boolean) => void;
  isMobile?: boolean;
  isTablet?: boolean;
}

export function Toolbar({ showEffectsRack, setShowEffectsRack, isMobile = false, isTablet = false }: ToolbarProps) {
  const activeView = useStudioStore((state) => state.activeView);
  const setActiveView = useStudioStore((state) => state.setActiveView);
  
  return (
    <div className="bg-[#252525] h-12 flex items-center px-2 md:px-3 gap-1 md:gap-2 border-b border-[#3a3a3a] overflow-x-auto">
      {/* File operations - Hidden on mobile */}
      {!isMobile && (
        <>
          <button className="p-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors" title="New">
            <FolderOpen size={18} />
          </button>
          <button className="p-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors" title="Save">
            <Save size={18} />
          </button>

          <div className="w-px h-8 bg-[#4a4a4a]" />
        </>
      )}

      {/* Edit operations - Hidden on mobile, reduced on tablet */}
      {!isMobile && (
        <>
          <button className="p-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors" title="Undo">
            <Undo size={18} />
          </button>
          <button className="p-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors" title="Redo">
            <Redo size={18} />
          </button>
          {!isTablet && (
            <>
              <button className="p-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors" title="Cut">
                <Scissors size={18} />
              </button>
              <button className="p-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors" title="Copy">
                <Copy size={18} />
              </button>
            </>
          )}

          <div className="w-px h-8 bg-[#4a4a4a]" />
        </>
      )}

      {/* View buttons - Always visible, responsive sizing */}
      <button
        onClick={() => setActiveView('pattern')}
        className={`p-2 rounded ${
          activeView === 'pattern' ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
        } transition-colors`}
        title="Pattern"
      >
        <LayoutGrid size={isMobile ? 18 : 20} />
      </button>
      <button
        onClick={() => setActiveView('pianoroll')}
        className={`p-2 rounded ${
          activeView === 'pianoroll' ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
        } transition-colors`}
        title="Piano Roll"
      >
        <Music2 size={isMobile ? 18 : 20} />
      </button>
      <button
        onClick={() => setActiveView('playlist')}
        className={`p-2 rounded ${
          activeView === 'playlist' ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
        } transition-colors`}
        title="Playlist"
      >
        <List size={isMobile ? 18 : 20} />
      </button>
      <button
        onClick={() => setActiveView('mixer')}
        className={`p-2 rounded ${
          activeView === 'mixer' ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
        } transition-colors`}
        title="Mixer"
      >
        <Sliders size={isMobile ? 18 : 20} />
      </button>
      
      <div className="flex-1" />

      {/* Presence Indicators - Show active users (Requirements 9.1) */}
      {!isMobile && (
        <div className="mr-2">
          <PresenceIndicators maxVisible={5} />
        </div>
      )}

      {/* Effects rack toggle - Hidden on mobile and tablet */}
      {!isMobile && !isTablet && (
        <button
          onClick={() => setShowEffectsRack(!showEffectsRack)}
          className={`p-2 rounded ${
            showEffectsRack ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
          } transition-colors`}
          title="Toggle Effects Rack"
        >
          <Menu size={20} />
        </button>
      )}
      
      {/* Pattern indicator - Hidden on mobile */}
      {!isMobile && (
        <div className="text-xs bg-[#3a3a3a] px-2 md:px-3 py-1 rounded flex items-center gap-1 md:gap-2">
          <span className="opacity-70 hidden md:inline">Pattern</span>
          <span className="text-[#ff6b00]">1 - 1</span>
        </div>
      )}
    </div>
  );
}